import React, { useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { RadioGroup } from '@headlessui/react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'
import { Input, InputGroup, InputLeftAddon } from '@wartek-id/input'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'
import { Shimmer } from '@wartek-id/shimmer'

import StateEmpty from 'app/KinerjaShared/components/StateEmpty'
import { Pagination } from 'app/KinerjaShared/components/Pagination'
import { PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import { Header } from 'app/KinerjaShared/components/Header'
import { useApiGetPelaksanaanList } from 'api/kinerja/gpk/pelaksanaan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { getPaginatedItems } from 'app/KinerjaShared/utils/pagination'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRowItem,
  TableRow,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import {
  GPK_PELAKSANAAN_GRADING_STATE_LABELS,
  GPK_PERILAKU_KERJA_GRADING_STATE_LABELS,
} from 'app/KinerjaGpkPage/constants/contents'
import {
  GpkPerilakuKerjaGradingStateEnum,
  GpkPraktikKinerjaGradingStateEnum,
} from 'types/kinerja-gpk'
import { parseQuery } from 'utils/router'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'

import type { MultiPeriodPayload } from 'types/kinerja'
import type { PelaksanaanItem } from 'types/kinerja-gpk'

const PAGE_QUERY_PARAM_KEY = 'page'
const SEARCH_QUERY_PARAM_KEY = 'name'
const PRAKTIK_KINERJA_STATUS_QUERY_PARAM_KEY = 'status'
const PAGE_SIZE = 10

type DaftarPemantauanKinerjaProps = {
  periodParams?: MultiPeriodPayload
}

type PraktikKinerjaStatusFilterEnum =
  | GpkPraktikKinerjaGradingStateEnum
  | AdditionalFilterEnum

enum AdditionalFilterEnum {
  ALL = 'Semua',
}

const PRAKTIK_KINERJA_STATUS_FILTER_LABELS: Record<
  PraktikKinerjaStatusFilterEnum,
  string
> = {
  [AdditionalFilterEnum.ALL]: 'Semua',
  ...GPK_PELAKSANAAN_GRADING_STATE_LABELS,
}

export function DaftarPemantauanKinerja({
  periodParams,
}: DaftarPemantauanKinerjaProps) {
  const router = useRouter()
  const { periodNo, periodYear } = usePeriodContext()
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: `${PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH}/pelaksanaan`,
  })

  //-----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get pelaksanaan list
  // -----------------------------------------
  const { isFetching: isPelaksanaanListLoading, data: pelaksanaanListData } =
    useApiGetPelaksanaanList({
      params: {
        skpPeriod: resolvedPeriodParams.skpPeriod,
        skpYear: resolvedPeriodParams.skpYear,
      },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // -----------------------------------------
  // Handle search
  // -----------------------------------------
  // Resolve search query param
  const searchQuery = useMemo(
    () =>
      parseQuery<string>(
        router.query[SEARCH_QUERY_PARAM_KEY] || undefined,
        'string'
      ),
    [router.query]
  )

  // Handle search change
  const handleSearchChange = (value: string) => {
    // Update URL
    const query = {
      ...router.query,
    }

    if (value.trim()) {
      query[SEARCH_QUERY_PARAM_KEY] = value
    } else {
      delete query[SEARCH_QUERY_PARAM_KEY]
    }

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    )
  }

  // -----------------------------------------
  // Handle Praktik Kinerja Status filter
  // -----------------------------------------
  // Resolve query param
  const praktikKinerjaStatusQuery = useMemo(() => {
    const value = parseQuery<string>(
      router.query[PRAKTIK_KINERJA_STATUS_QUERY_PARAM_KEY] || undefined,
      'string'
    )

    const isValidValue = Object.keys(
      PRAKTIK_KINERJA_STATUS_FILTER_LABELS
    ).includes(value)

    if (!isValidValue) {
      // Return "Semua" as default
      return AdditionalFilterEnum.ALL
    }

    return value
  }, [router.query])

  // Resolve counts
  const statusFilterCounts = useMemo(() => {
    const pelaksanaanItems = pelaksanaanListData?.data || []
    const counts: Record<PraktikKinerjaStatusFilterEnum, number> = {} as Record<
      PraktikKinerjaStatusFilterEnum,
      number
    >

    // Initialize all filter counts with 0
    Object.keys(PRAKTIK_KINERJA_STATUS_FILTER_LABELS).forEach((key) => {
      counts[key as PraktikKinerjaStatusFilterEnum] = 0
    })

    // Count for "Semua" (all items)
    counts[AdditionalFilterEnum.ALL] = pelaksanaanItems.length

    // Count for each specific status
    pelaksanaanItems.forEach((pelaksanaanItem) => {
      const observationDate = pelaksanaanItem.observation?.date
      const hasObservationDate = checkObservationDate(observationDate)

      const gradingState = pelaksanaanItem.observation?.gradingState
      const isValidGradingState = counts.hasOwnProperty(gradingState)

      if (hasObservationDate && isValidGradingState) {
        counts[gradingState]++
      }
    })

    return counts
  }, [pelaksanaanListData?.data])

  // Handle status filter change
  const handlePraktikKinerjaStatusFilterChange = (value: string) => {
    // Update URL
    const query = {
      ...router.query,
    }
    query[PRAKTIK_KINERJA_STATUS_QUERY_PARAM_KEY] = value

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    )
  }

  // -----------------------------------------
  // Handle pagination
  // -----------------------------------------
  // Resolve query param
  const pageQuery = useMemo(
    () =>
      parseQuery<number>(
        router.query[PAGE_QUERY_PARAM_KEY] || undefined,
        'string'
      ) || 1,
    [router.query]
  )

  // Handle page change
  const handlePageChange = (page: number) => {
    // Update URL
    const query = {
      ...router.query,
      [PAGE_QUERY_PARAM_KEY]: page.toString(),
    }

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    )
  }

  // -----------------------------------------
  // Resolve filtered items
  // -----------------------------------------
  const hasFilter = useMemo(() => {
    return !!searchQuery || !!praktikKinerjaStatusQuery
  }, [searchQuery, praktikKinerjaStatusQuery])

  const filteredPelaksanaanList = useMemo(() => {
    let items = pelaksanaanListData?.data || []

    if (hasFilter) {
      items = items.filter((pelaksanaanItem) => {
        // Search query filter
        let searchQueryFound = true
        if (searchQuery) {
          searchQueryFound = pelaksanaanItem.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        }

        // Praktik Kinerja Status filter
        let praktikKinerjaStatusFilterFound = true
        if (
          // Only filter if the query is not "Semua"
          praktikKinerjaStatusQuery &&
          praktikKinerjaStatusQuery !== AdditionalFilterEnum.ALL
        ) {
          // Observation date
          const observationDate = pelaksanaanItem.observation?.date
          const hasObservationDate = checkObservationDate(observationDate)

          // Match grading state
          const isGradingStateMatch =
            String(pelaksanaanItem.observation?.gradingState).toLowerCase() ===
            String(praktikKinerjaStatusQuery).toLowerCase()

          // Resolve value
          praktikKinerjaStatusFilterFound =
            hasObservationDate && isGradingStateMatch
        }

        return searchQueryFound && praktikKinerjaStatusFilterFound
      })
    }

    return items
  }, [
    pelaksanaanListData?.data,
    searchQuery,
    praktikKinerjaStatusQuery,
    hasFilter,
  ])

  // -----------------------------------------
  // Resolve paginated items
  // -----------------------------------------
  const paginatedPelaksanaanList = useMemo(() => {
    return getPaginatedItems<PelaksanaanItem>(
      filteredPelaksanaanList,
      pageQuery,
      PAGE_SIZE
    )
  }, [filteredPelaksanaanList, pageQuery])

  const isEmpty = paginatedPelaksanaanList.length === 0
  const showPagination = !isPelaksanaanListLoading && !isEmpty

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between items-center">
          <Header
            title="Pemantauan Kinerja"
            className="mb-9"
            onBack={handleBack}
          />
        </div>

        <section className="bg-white shadow-card rounded-lg border border-subdued">
          <div className="py-4 px-6 space-y-4">
            <div className="flex items-center justify-between gap-6 ">
              <Text variant="heading-md" as="h2">
                Lakukan Pemantauan serta cek status Praktik Kinerja dan Perilaku
                Kerja
              </Text>

              <div className="max-w-72">
                <InputGroup>
                  <InputLeftAddon>
                    <Icon>search</Icon>
                  </InputLeftAddon>
                  <Input
                    placeholder={
                      isPelaksanaanListLoading ? 'Memuat...' : 'Cari'
                    }
                    size="sm"
                    clearable
                    className="!pr-12"
                    disabled={isPelaksanaanListLoading}
                    value={searchQuery || ''}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </InputGroup>
              </div>
            </div>

            {isPelaksanaanListLoading ? (
              <Shimmer width={720} height={32} />
            ) : (
              <RadioGroup
                onChange={handlePraktikKinerjaStatusFilterChange}
                value={praktikKinerjaStatusQuery}
                className="flex gap-4"
              >
                {Object.keys(PRAKTIK_KINERJA_STATUS_FILTER_LABELS).map(
                  (key) => (
                    <RadioGroup.Option
                      value={key}
                      key={key}
                      as={React.Fragment}
                    >
                      {({ checked }) => (
                        <Button
                          type={undefined}
                          size="sm"
                          color="white"
                          className={clsx(
                            '!rounded-full',
                            checked
                              ? 'bg-surface-selected-default border-blue-60 !text-blue-70  !outline-blue-70'
                              : 'bg-white border-subdued'
                          )}
                        >
                          {PRAKTIK_KINERJA_STATUS_FILTER_LABELS[key]} (
                          {statusFilterCounts[
                            key as PraktikKinerjaStatusFilterEnum
                          ] ?? 0}
                          )
                        </Button>
                      )}
                    </RadioGroup.Option>
                  )
                )}
              </RadioGroup>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableHeaderItem className="text-sm max-w-60">
                Nama
              </TableHeaderItem>
              <TableHeaderItem className="text-sm">Jabatan</TableHeaderItem>
              <TableHeaderItem className="text-sm">
                Jadwal Observasi Praktik Kinerja
              </TableHeaderItem>
              <TableHeaderItem className="text-sm">
                Status Praktik Kinerja
              </TableHeaderItem>
              <TableHeaderItem className="text-sm">
                Status Perilaku Kerja
              </TableHeaderItem>
              <TableHeaderItem className="text-sm"></TableHeaderItem>
            </TableHeader>
            <TableBody>
              {isPelaksanaanListLoading ? (
                // Loading state
                <TableShimmer columns={5} rows={7} />
              ) : (
                // Show pelaksanaan items
                paginatedPelaksanaanList.map((pelaksanaanItem) => {
                  const observationDate = pelaksanaanItem.observation?.date
                  const hasObservationDate =
                    checkObservationDate(observationDate)

                  const praktikKinerjaGradingState =
                    pelaksanaanItem.observation?.gradingState
                  const perilakuKerjaGradingState =
                    pelaksanaanItem.workBehavior?.gradingState

                  const isPraktikKinerjaGraded =
                    praktikKinerjaGradingState ===
                    GpkPraktikKinerjaGradingStateEnum.SELESAI
                  const isPerilakuKerjaGraded =
                    perilakuKerjaGradingState ===
                    GpkPerilakuKerjaGradingStateEnum.SELESAI

                  const isCompleted =
                    isPraktikKinerjaGraded && isPerilakuKerjaGraded

                  return (
                    <TableRow key={pelaksanaanItem.userId}>
                      <TableRowItem className="overflow-wrap-anywhere align-middle max-w-96">
                        {pelaksanaanItem.name}
                      </TableRowItem>
                      <TableRowItem className="align-middle">
                        Guru Pendidikan Khusus
                      </TableRowItem>
                      {hasObservationDate ? (
                        <>
                          <TableRowItem className="align-middle">
                            {hasObservationDate ? observationDate : '-'}
                          </TableRowItem>
                          <TableRowItem className="align-middle">
                            <Badge
                              variant={
                                isPraktikKinerjaGraded ? 'success' : 'neutral'
                              }
                            >
                              {
                                GPK_PELAKSANAAN_GRADING_STATE_LABELS[
                                  praktikKinerjaGradingState
                                ]
                              }
                            </Badge>
                          </TableRowItem>
                          <TableRowItem className="align-middle">
                            <Badge
                              className="whitespace-nowrap"
                              variant={
                                isPerilakuKerjaGraded ? 'success' : 'critical'
                              }
                            >
                              {
                                GPK_PERILAKU_KERJA_GRADING_STATE_LABELS[
                                  perilakuKerjaGradingState
                                ]
                              }
                            </Badge>
                          </TableRowItem>
                        </>
                      ) : (
                        <TableRowItem className="align-middle" colSpan={3}>
                          Pelaksanaan belum dimulai. Beri himbauan untuk segera
                          memulai dan mengisi Diskusi Persiapan.
                        </TableRowItem>
                      )}

                      <TableRowItem className="align-middle">
                        <Link
                          href={`${PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH}/pelaksanaan/pemantauan/${pelaksanaanItem.ptkId}`}
                          passHref
                        >
                          <Button
                            size="sm"
                            as="a"
                            type={undefined}
                            color={isCompleted ? 'white' : 'black'}
                            className="!inline-flex"
                            disabled={!hasObservationDate}
                          >
                            {isCompleted ? 'Cek' : 'Lakukan'}
                          </Button>
                        </Link>
                      </TableRowItem>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>

          {/* Empty state */}
          {isEmpty && !isPelaksanaanListLoading && (
            <div className="py-10 px-6">
              <StateEmpty
                title={hasFilter ? 'Tidak ada hasil' : 'Belum ada data'}
              >
                <Text color="subdued">
                  {hasFilter
                    ? 'Pastikan ejaan yang diketik benar dan guru sudah menyelesaikan tahap Perencanaan Kinerja.'
                    : 'Belum ada guru yang sudah menyelesaikan tahap Perencanaan Kinerja. Jika ada, namanya akan tampil di sini.'}
                </Text>
              </StateEmpty>
            </div>
          )}

          {showPagination && (
            <div className="border-t border-subdued py-5 px-6">
              <Pagination
                totalItems={filteredPelaksanaanList.length}
                pageSize={PAGE_SIZE}
                currentPage={pageQuery}
                onPageChange={handlePageChange}
                itemLabel="nama"
              />
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

const NO_OBSERVATION_DATE_VALUE = 'Belum ada jadwal'

function checkObservationDate(observationDate: string) {
  return !!observationDate && observationDate !== NO_OBSERVATION_DATE_VALUE
}
